import type { ColorScheme } from '../../types'

export type { ColorScheme }

export type StatusPillVariant =
  | 'teal'
  | 'teal-blue'
  | 'teal-green'
  | 'complete'
  | 'in-progress'
  | 'incomplete'
  | 'case-active'
  | 'case-closed'
  | 'error'
  | 'success'
  | 'warning'
  | 'workflow-draft'
  | 'workflow-published'
  | 'workflow-archived'

type VariantTokens = {
  bg: string
  color: string
  border?: string
  dotColor?: string
}
type SchemeAwareTokens = VariantTokens | Record<ColorScheme, VariantTokens>

export const VARIANT_TOKENS: Record<StatusPillVariant, SchemeAwareTokens> = {
  teal: {
    dark: { bg: 'device.associated-bg', color: 'device.associated-icon' },
    light: {
      bg: 'light.device.associated-bg',
      color: 'light.device.associated-icon',
    },
  },
  'teal-blue': {
    dark: { bg: 'device.grid-enabled-bg', color: 'device.grid-enabled-icon' },
    light: {
      bg: 'light.device.grid-enabled-bg',
      color: 'light.device.grid-enabled-icon',
    },
  },
  'teal-green': {
    dark: {
      bg: 'device.tailgate-enabled-bg',
      color: 'device.tailgate-enabled-icon',
    },
    light: {
      bg: 'light.device.tailgate-enabled-bg',
      color: 'light.device.tailgate-enabled-icon',
    },
  },
  complete: {
    dark: { bg: 'status.resolved-bg', color: 'status.resolved-text' },
    light: {
      bg: 'light.status.resolved-bg',
      color: 'light.status.resolved-text',
    },
  },
  'in-progress': {
    dark: { bg: 'status.in-progress-bg', color: 'status.in-progress-text' },
    light: {
      bg: 'light.status.in-progress-bg',
      color: 'light.status.in-progress-text',
    },
  },
  incomplete: {
    dark: { bg: 'status.active-bg', color: 'status.active-text' },
    light: {
      bg: 'light.status.active-bg',
      color: 'light.status.active-text',
    },
  },
  // Case status. Active reads as attention-needed (red); Closed reads as
  // done (green). Reuses the error / resolved status tokens rather than
  // introducing new ones — kept as distinct variants so the case domain's
  // vocabulary is explicit at the call site and does not collide with the
  // blue tour/task-lifecycle `incomplete`/`status.active-*` tokens.
  'case-active': {
    dark: { bg: 'status.error-bg', color: 'status.error-text' },
    light: {
      bg: 'light.status.error-bg',
      color: 'light.status.error-text',
    },
  },
  'case-closed': {
    dark: { bg: 'status.resolved-bg', color: 'status.resolved-text' },
    light: {
      bg: 'light.status.resolved-bg',
      color: 'light.status.resolved-text',
    },
  },
  error: {
    dark: { bg: 'status.error-bg', color: 'status.error-text' },
    light: {
      bg: 'light.status.error-bg',
      color: 'light.status.error-text',
    },
  },
  success: {
    dark: { bg: 'status.success-bg', color: 'status.success-text' },
    light: {
      bg: 'light.status.success-bg',
      color: 'light.status.success-text',
    },
  },
  warning: {
    dark: { bg: 'status.warning-bg', color: 'status.warning-text' },
    light: {
      bg: 'light.status.warning-bg',
      color: 'light.status.warning-text',
    },
  },
  'workflow-draft': {
    dark: {
      bg: 'bg.card-elevated',
      color: 'text.primary',
      dotColor: 'status.workflow-draft',
    },
    light: {
      bg: 'light.bg.card-elevated',
      color: 'light.text.primary',
      dotColor: 'light.status.workflow-draft',
    },
  },
  'workflow-published': {
    dark: {
      bg: 'bg.card-elevated',
      color: 'text.primary',
      dotColor: 'status.workflow-published',
    },
    light: {
      bg: 'light.bg.card-elevated',
      color: 'light.text.primary',
      dotColor: 'light.status.workflow-published',
    },
  },
  'workflow-archived': {
    dark: {
      bg: 'bg.card-elevated',
      color: 'text.primary',
      dotColor: 'status.workflow-archived',
    },
    light: {
      bg: 'light.bg.card-elevated',
      color: 'light.text.primary',
      dotColor: 'light.status.workflow-archived',
    },
  },
}

// Returns scheme-specific tokens if the variant defines separate dark/light values, otherwise returns the shared tokens.
export const resolveTokens = (
  tokens: SchemeAwareTokens,
  colorScheme: ColorScheme
): VariantTokens => ('dark' in tokens ? tokens[colorScheme] : tokens)
